import { CircularProgress, Paper, TableContainer } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useState } from "react";

import { MemberAdminSummaryWithHierarchy } from "@ses-mams/api-contract";
import {
  formatMemberFullName,
  useDebouncedSearch,
} from "@ses-mams/react-utils";

import { Heading } from "~/components/ui/heading";
import { SearchInput } from "~/components/ui/searchInput";
import { Stack } from "~/components/ui/stack";

import { ActionsCell } from "./components/ActionsCell";
import { RolesList } from "./components/RolesList";
import { useMemberPermissionsListData } from "./hooks";
import { OrgHierarchyList } from "../memberDetails/OrgHierarchyList";
import { SearchUsersFilters } from "./SearchUsersFilters";

const columns: GridColDef<MemberAdminSummaryWithHierarchy>[] = [
  {
    field: "name",
    headerName: "Name and ID",
    sortable: false,
    flex: 2,
    valueGetter: (_, row) => formatMemberFullName(row),
    renderCell: params => (
      <Stack gap="small">
        <div>{formatMemberFullName(params.row)}</div>
        <div>{params.row.id}</div>
      </Stack>
    ),
  },
  {
    field: "locations",
    headerName: "Locations",
    sortable: false,
    flex: 2,
    renderCell: params => (
      <Stack gap="small">
        <OrgHierarchyList hierarchy={params.row.hierarchy} size="small" />
      </Stack>
    ),
  },
  {
    field: "syncedRoles",
    headerName: "Synced roles",
    sortable: false,
    renderCell: params =>
      params.row.activeHigherDutiesAllowance ? (
        <RolesList
          roles={params.row.activeHigherDutiesAllowance.roles}
          active={!params.row.rolesOverride}
          direction="column"
        />
      ) : params.row.syncedRoles.length ? (
        <RolesList
          roles={params.row.syncedRoles}
          active={!params.row.rolesOverride}
          direction="column"
        />
      ) : (
        "None"
      ),
    flex: 2,
  },
  {
    field: "rolesOverride",
    headerName: "Override roles",
    sortable: false,
    renderCell: params =>
      params.row.rolesOverride &&
      (params.row.rolesOverride.length ? (
        <RolesList roles={params.row.rolesOverride} active direction="column" />
      ) : (
        "Set to None"
      )),
    flex: 2,
  },
  {
    field: "isActive",
    headerName: "Enabled",
    sortable: false,
    flex: 1,
    valueGetter: (_, row) => (row.isActive ? "Yes" : "No"),
  },
  {
    field: "actions",
    headerName: "",
    sortable: false,
    flex: 1.5,
    renderCell: params => <ActionsCell member={params.row} />,
  },
];

export const SearchUsersList = () => {
  const { searchValue, setSearchValue, debouncedSearchValue } =
    useDebouncedSearch("");

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });

  const { isLoading, isRefetching, data, totalCount } =
    useMemberPermissionsListData({
      searchValue: debouncedSearchValue,
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
    });

  return (
    <Stack
      gap="large"
      paddingTop="large"
      direction="column"
      sx={{ height: "100%" }}
    >
      <Stack
        direction="row"
        gap="medium"
        align="center"
        justify="space-between"
      >
        <Heading level="2">Admin - Users</Heading>

        <SearchUsersFilters />
      </Stack>
      <SearchInput
        placeholder="Search"
        value={searchValue}
        onValueChange={setSearchValue}
      />
      {isLoading || isRefetching ? (
        <Stack justify="center" align="center" sx={{ height: "100%" }}>
          <CircularProgress />
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <DataGrid
            rows={data}
            columns={columns}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={model => setPaginationModel(model)}
            rowCount={totalCount}
            disableColumnMenu
            rowSelection={false}
            rowHeight={100}
            getRowHeight={() => "auto"}
            sx={{
              "&.MuiDataGrid-root": {
                minHeight: 200,
              },
              "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                py: "16px",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
            }}
          />
        </TableContainer>
      )}
    </Stack>
  );
};
